import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Send, Square, ShoppingBag, Headset, Image as ImageIcon, X, MoreHorizontal, Plus, Mic, Paperclip, RotateCcw, MicOff, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SessionRatingButton from './SessionRatingButton';
import PDFThumbnail from './PDFThumbnail';
import PDFViewer from './PDFViewer';
import { useAudioRecorder } from '../../../hooks/useAudioRecorder';
import RecordingWaveform from './RecordingWaveform';
import { useSidebarContext } from '../../../layout/AppLayout';

interface ChatInputProps {
  inputMessage: string;
  selectedImagePreview: string | null;
  selectedFiles?: Array<{ data: string; mimeType: string; preview: string; name: string; gcsUri?: string; isUploading?: boolean }>;
  isLoading: boolean;
  /** True when the org's monthly LLM-query quota is reached and enforced —
   *  the composer is disabled with an explanatory placeholder. */
  quotaPaused?: boolean;
  /** A structured diagram clarification is waiting. The user must answer on
   * the map instead of bypassing it with another chat message. */
  mapChoiceRequired?: boolean;
  activeSessionId: string | null;
  hasDraftOrders: boolean;
  hasSubmittedRating: boolean;
  hasMessages: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  imageInputRef: React.RefObject<HTMLInputElement | null>;
  fileInputRef?: React.RefObject<HTMLInputElement | null>;
  onInputChange: (value: string) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onSend: () => void;
  onStop: () => void;
  onImageSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFileSelect?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearImage: () => void;
  onClearFile?: (index: number) => void;
  onShowMobileOrders: () => void;
  onShowMobileCart: () => void;
  onShowSessionRating: () => void;
  onRequestAssistance: () => void;
  onResetChat?: () => void;
  onAudioFile?: (base64: string, mimeType: string) => void;
  onAudioSend?: (audioFile: { data: string; mimeType: string; preview: string; name: string }) => void;
  cartItemsCount?: number;
  retryState?: {
    isRetrying: boolean;
    attempt: number;
    maxAttempts: number;
    reason: string;
  } | null;
  onRetryLastMessage?: () => void;
  showRetryPrompt?: boolean;
  lastMessageHasError?: boolean;
  errorMessage?: string | null;
  /** How much right-side space to reserve for the cart panel so the composer
   *  aligns with the (variable-width) cart: 'none' (no cart), 'collapsed'
   *  (minimized strip), or 'expanded' (full panel). */
  cartWidthMode?: 'none' | 'collapsed' | 'expanded';
}

const ChatInput: React.FC<ChatInputProps> = ({
  inputMessage,
  selectedImagePreview,
  selectedFiles = [],
  isLoading,
  quotaPaused = false,
  mapChoiceRequired = false,
  activeSessionId,
  hasDraftOrders,
  hasSubmittedRating,
  hasMessages,
  inputRef,
  imageInputRef,
  fileInputRef,
  onInputChange,
  onKeyPress,
  onSend,
  onStop,
  onImageSelect,
  onFileSelect,
  onClearImage,
  onClearFile,
  onShowMobileOrders,
  onShowMobileCart,
  onShowSessionRating,
  onRequestAssistance,
  onResetChat,
  onAudioFile,
  onAudioSend,
  cartItemsCount = 0,
  retryState,
  onRetryLastMessage,
  showRetryPrompt = false,
  lastMessageHasError = false,
  errorMessage,
  cartWidthMode = 'expanded',
}) => {
  const { t } = useTranslation();
  // The bar is fixed, so the conversation must reserve its height — measured,
  // not guessed: attachments, the quota banner and the toolbar all change it.
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const publish = () => document.documentElement.style.setProperty('--composer-h', `${el.offsetHeight}px`);
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => { ro.disconnect(); document.documentElement.style.removeProperty('--composer-h'); };
  }, []);
  // Left offset must match the AppLayout orders sidebar (w-20 minimized /
  // w-[212px] expanded, lg+ only); right offset must match the cart width. This
  // keeps the fixed composer aligned with the message column between them.
  const { isOrdersPanelMinimized } = useSidebarContext();
  const sidebarLeftClass = isOrdersPanelMinimized ? 'lg:left-20' : 'lg:left-[212px]';
  const cartRightClass = cartWidthMode === 'expanded'
    ? 'md:right-[28rem] lg:right-[34rem] xl:right-[42rem]'
    : cartWidthMode === 'collapsed'
      ? 'md:right-14'
      : '';
  // Re-focus the composer when a turn finishes. The textarea is disabled
  // while streaming and browsers blur disabled elements, so without this the
  // user must click back into the input before typing the next message.
  // Guards: only on the true→false isLoading edge (never on mount), only when
  // the composer is actually usable, never on touch-primary devices (focus
  // pops the on-screen keyboard), and never stealing focus the user has
  // already placed on another interactive element (a clarification form
  // field, a modal input, …).
  const wasLoadingRef = useRef(isLoading);
  useEffect(() => {
    const turnJustFinished = wasLoadingRef.current && !isLoading;
    wasLoadingRef.current = isLoading;
    if (!turnJustFinished || quotaPaused || mapChoiceRequired || !activeSessionId) return;
    if (typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)')?.matches) return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;
    inputRef.current?.focus();
  }, [isLoading, quotaPaused, mapChoiceRequired, activeSessionId, inputRef]);

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [zoomedImage, setZoomedImage] = useState<{ preview: string; name: string; mimeType?: string } | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);

  const { status: audioStatus, isSupported: audioSupported, toggle: toggleAudio, stop: stopAudio } = useAudioRecorder({
    onAudioFile: (base64, mimeType) => {
      const ext = mimeType.includes('ogg') ? 'ogg' : mimeType.includes('mp4') ? 'mp4' : 'webm';
      const audioFile = {
        data: base64,
        mimeType,
        preview: '',
        name: `voice-message.${ext}`,
      };
      if (onAudioSend) {
        // Direct send path — bypasses selectedFiles state so there's no race condition
        onAudioSend(audioFile);
      } else if (onAudioFile) {
        onAudioFile(base64, mimeType);
      }
    },
    onError: (msg) => {
      setAudioError(msg);
      setTimeout(() => setAudioError(null), 4000);
    },
  });

  const isRecording = audioStatus === 'recording';
  const isProcessingAudio = audioStatus === 'processing';
  const isAnyFileUploading = (selectedFiles ?? []).some((f) => f.isUploading);

  // Auto-grow the multiline textarea with its content, up to a max height then
  // scroll. Runs whenever the text changes or the textarea (re)mounts.
  const MAX_INPUT_HEIGHT = 160; // px ≈ 6 lines
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_HEIGHT)}px`;
  }, [inputMessage, isRecording, inputRef]);

  const handleSend = () => {
    if (isRecording) stopAudio();
    if (isAnyFileUploading) return;
    onSend();
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!onFileSelect || !fileInputRef?.current) return;

    const pastedImages = Array.from(event.clipboardData.files).filter((file) =>
      file.type.startsWith('image/')
    );
    if (pastedImages.length === 0) return;

    // Keep pasted images on the exact same validation/upload path as files
    // selected with the attachment button.
    event.preventDefault();
    const transfer = new DataTransfer();
    pastedImages.forEach((file) => transfer.items.add(file));
    fileInputRef.current.files = transfer.files;
    fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const getPlaceholder = () => {
    if (mapChoiceRequired) {
      return t('chat.clarification.chooseFromMapPlaceholder', { defaultValue: 'Expand the map above and choose a part to continue' });
    }
    return t('chat.placeholder.typeMessage');
  };

  const handleImageClick = (file: { preview: string; name: string; mimeType: string; data?: string }) => {
    // Allow zoom for images and PDFs, not TXT files
    const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (imageTypes.includes(file.mimeType)) {
      setZoomedImage({ preview: file.preview, name: file.name, mimeType: file.mimeType });
    } else if (file.mimeType === 'application/pdf' && file.data) {
      // For PDFs, we need the full data URL
      setZoomedImage({ preview: `data:${file.mimeType};base64,${file.data}`, name: file.name, mimeType: file.mimeType });
    }
  };


  return (
    <>
      <div ref={barRef} className={`fixed bottom-0 left-0 right-0 ${sidebarLeftClass} ${cartRightClass} backdrop-blur-sm border-t border-gray-200/50 z-40 transition-all duration-300 ease-in-out`}>
        <div className="max-w-3xl mx-auto px-3 py-2 sm:px-6 sm:py-3">

          {/* Multiple Files Preview */}
          {selectedFiles.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-3">
              {selectedFiles.map((file, index) => (
                <div key={index} className="relative inline-block group">
                  {/* Check if it's an image or a document */}
                  {file.mimeType.startsWith('image/') ? (
                    <img
                      src={file.preview}
                      alt={file.name || `File ${index + 1}`}
                      className="max-h-28 rounded-2xl shadow-sm border border-gray-100 transition-all group-hover:shadow-md cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageClick(file);
                      }}
                    />
                  ) : file.mimeType === 'application/pdf' ? (
                    <div
                      className="h-28 w-24 rounded-2xl overflow-hidden shadow-sm border border-gray-100 transition-all group-hover:shadow-md cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageClick(file);
                      }}
                    >
                      <PDFThumbnail
                        file={`data:${file.mimeType};base64,${file.data}`}
                        maxHeight={112}
                      />
                    </div>
                  ) : (
                    <div className="h-28 w-24 rounded-2xl shadow-sm border border-gray-100 transition-all group-hover:shadow-md flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                      <svg className="w-10 h-10 text-blue-500 mb-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                      </svg>
                      <span className="text-xs font-semibold text-blue-600">TXT</span>
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onClearFile?.(index);
                    }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all opacity-0 group-hover:opacity-100 z-10"
                    title={t('chat.removeFile', { defaultValue: 'Remove file' })}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  {/* Upload-in-progress overlay */}
                  {file.isUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl z-20 pointer-events-none">
                      <svg className="animate-spin w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    </div>
                  )}
                  {file.name && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent text-white text-xs p-2 rounded-b-2xl truncate pointer-events-none">
                      {file.name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Legacy Single Image Preview */}
          {selectedImagePreview && selectedFiles.length === 0 && (
            <div className="mb-4 relative inline-block group">
              <img
                src={selectedImagePreview}
                alt="Selected image preview"
                className="max-h-36 rounded-2xl shadow-sm border border-gray-100 transition-all group-hover:shadow-md cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setZoomedImage({ preview: selectedImagePreview, name: 'Selected image', mimeType: 'image/jpeg' });
                }}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClearImage();
                }}
                className="absolute -top-2 -right-2 w-7 h-7 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-all opacity-0 group-hover:opacity-100 z-10"
                title={t('chat.removeImage')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Error / Retry Banner - shown above the input when there's an error or no response */}
          {showRetryPrompt && !isLoading && onRetryLastMessage && (
            <div className={`mb-2 flex items-center gap-2 ${lastMessageHasError ? 'bg-red-50 border border-red-200' : 'bg-orange-50 border border-orange-200'} rounded-2xl px-4 py-2.5 text-sm`}>
              <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 flex-shrink-0 ${lastMessageHasError ? 'text-red-500' : 'text-orange-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className={`${lastMessageHasError ? 'text-red-800' : 'text-orange-800'} flex-1`}>
                {lastMessageHasError
                  ? (errorMessage || t('chat.errorRetry', { defaultValue: 'Something went wrong.' }))
                  : t('chat.noResponseRetry', { defaultValue: 'No response received.' })}
              </span>
              <button
                onClick={onRetryLastMessage}
                className={`flex items-center gap-1.5 ${lastMessageHasError ? 'bg-red-100 hover:bg-red-200 text-red-800' : 'bg-orange-100 hover:bg-orange-200 text-orange-800'} font-medium px-3 py-1 rounded-lg transition-colors text-sm flex-shrink-0`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {t('chat.retry', { defaultValue: 'Retry' })}
              </button>
            </div>
          )}

          {/* Modern Input Container - Compact two-line on mobile, full on desktop */}
          <div className="relative group">
            {/* Retry Banner - shown inside the input area when retrying */}
            {retryState?.isRetrying && (
              <div className="mb-2 flex items-center gap-2 bg-yellow-50 border border-yellow-200 rounded-2xl px-4 py-2.5 text-sm">
                <svg className="animate-spin h-4 w-4 flex-shrink-0 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="font-medium text-yellow-800 truncate">
                  {retryState.reason} — Retrying ({retryState.attempt}/{retryState.maxAttempts})
                </span>
              </div>
            )}
            <div className={`bg-white rounded-3xl sm:shadow-[0_2px_12px_rgba(0,0,0,0.08)] border border-gray-200/60 transition-all duration-300 group-hover:shadow-[0_4px_20px_rgba(0,0,0,0.12)] group-focus-within:shadow-[0_4px_20px_rgba(0,0,0,0.12)] group-focus-within:border-gray-300/80 ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}`}>
              {/* Input Area */}
              <div className="p-2 sm:p-4">
                {/* Text Input with Send Button */}
                <div className="flex items-end gap-1 sm:gap-2">
                  <div className="flex-1 flex items-end">
                    {isRecording ? (
                      <RecordingWaveform onStop={stopAudio} className="pl-2" />
                    ) : (
                      <>
                        {isLoading && (
                          <div className="flex-shrink-0 mr-2 mb-1.5">
                            <div className="flex gap-1">
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                            </div>
                          </div>
                        )}
                        <textarea
                          ref={inputRef}
                          rows={1}
                          value={inputMessage}
                          onChange={(e) => onInputChange(e.target.value)}
                          onKeyDown={onKeyPress}
                          onPaste={handlePaste}
                          placeholder={
                            quotaPaused
                              ? t('usage.pausedPlaceholder', { defaultValue: 'Monthly LLM query limit reached — upgrade to continue' })
                              : isProcessingAudio
                              ? t('chat.audio.processing', { defaultValue: 'Processing audio…' })
                              : isLoading
                              ? t('chat.placeholder.processing', { defaultValue: 'Processing...' })
                              : getPlaceholder()
                          }
                          style={{ maxHeight: `${MAX_INPUT_HEIGHT}px` }}
                          className={`w-full resize-none overflow-y-auto bg-transparent placeholder-gray-400/70 focus:outline-none text-base leading-relaxed pl-2 sm:pl-3 py-1 ${isLoading || quotaPaused || mapChoiceRequired ? 'cursor-not-allowed' : ''} text-gray-900`}
                          disabled={isLoading || quotaPaused || mapChoiceRequired || !activeSessionId}
                        />
                      </>
                    )}
                  </div>

                  {/* Send/Stop Button — hidden while recording (waveform has its own stop) */}
                  {!isRecording && (
                  <div className="flex-shrink-0">
                    {isLoading ? (
                      <button
                        onClick={onStop}
                        className="p-1.5 sm:p-2 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-all shadow-lg hover:shadow-xl animate-pulse"
                        title={t('chat.stop', { defaultValue: 'Stop' })}
                      >
                        <Square className="w-4 h-4 sm:w-5 sm:h-5 fill-white" />
                      </button>
                    ) : (
                      <button
                        onClick={handleSend}
                        disabled={mapChoiceRequired || (!inputMessage.trim() && selectedFiles.length === 0 && !selectedImagePreview) || !activeSessionId || isAnyFileUploading || quotaPaused}
                        className="p-1.5 sm:p-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-gray-900 shadow-sm"
                        title={isAnyFileUploading ? t('chat.uploadingFiles', { defaultValue: 'Uploading files…' }) : t('chat.send', { defaultValue: 'Send' })}
                      >
                        {isAnyFileUploading
                          ? <svg className="animate-spin w-4 h-4 sm:w-5 sm:h-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                          : <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                        }
                      </button>
                    )}
                  </div>
                  )}
                </div>

                {/* Bottom Action Buttons — hidden while recording */}
                {!isRecording && (
                <div className="mt-1.5 sm:mt-2 pt-1.5 sm:pt-2 border-t border-gray-100">
                  {/* Hidden file inputs */}
                  {fileInputRef && onFileSelect && (
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,.pdf,.txt,text/plain,application/pdf"
                      onChange={onFileSelect}
                      multiple
                      className="hidden"
                    />
                  )}
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={onImageSelect}
                    multiple
                    className="hidden"
                  />

                  {/* Action Buttons Row - compact on mobile */}
                  <div className="flex items-center gap-0.5 sm:gap-1">
                    {/* Upload File Button */}
                    <button
                      onClick={() => fileInputRef?.current?.click()}
                      disabled={isLoading || mapChoiceRequired || !activeSessionId}
                      className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100/80 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                      title={t('chat.uploadFile', { defaultValue: 'Upload Image or File' })}
                    >
                      <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>

                    {/* Mic / Voice Input Button */}
                    {audioSupported && (
                      <button
                        onClick={toggleAudio}
                        disabled={isLoading || mapChoiceRequired || !activeSessionId || isProcessingAudio}
                        className={`p-1.5 sm:p-2 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                          isRecording
                            ? 'text-red-500 bg-red-50 hover:bg-red-100 animate-pulse'
                            : isProcessingAudio
                            ? 'text-blue-500 bg-blue-50'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/80'
                        }`}
                        title={isRecording
                          ? t('chat.audio.stopRecording', { defaultValue: 'Stop recording' })
                          : t('chat.audio.startRecording', { defaultValue: 'Record audio message' })
                        }
                      >
                        {isRecording ? (
                          <MicOff className="w-4 h-4 sm:w-5 sm:h-5" />
                        ) : (
                          <Mic className="w-4 h-4 sm:w-5 sm:h-5" />
                        )}
                      </button>
                    )}

                    {/* Menu Button */}
                    <button
                      onClick={() => setShowMobileMenu(!showMobileMenu)}
                      className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100/80 rounded-xl transition-all"
                      title={t('chat.actions.title')}
                    >
                      <MoreHorizontal className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>

                    {/* Right-side buttons */}
                    <div className="ml-auto flex items-center gap-0.5 sm:gap-1">
                      {/* Rate Session Button */}
                      {hasMessages && (
                        <button
                          onClick={onShowSessionRating}
                          disabled={hasSubmittedRating}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-xs font-semibold transition-all disabled:cursor-default ${
                            hasSubmittedRating
                              ? 'opacity-60 cursor-default'
                              : 'hover:bg-purple-50'
                          }`}
                          title={hasSubmittedRating ? t('chat.actions.ratingSubmitted') : t('chat.actions.rateSession')}
                        >
                          <span className={`flex items-center gap-1 px-1.5 py-1 rounded-lg ${hasSubmittedRating ? 'bg-purple-100' : 'bg-purple-100 hover:bg-purple-200'} transition-all`}>
                            <ThumbsUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600" />
                            <ThumbsDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-500 opacity-70" />
                          </span>
                          <span className="hidden sm:inline text-purple-600">
                            {hasSubmittedRating ? t('chat.actions.ratingSubmitted') : t('chat.actions.rateSession')}
                          </span>
                        </button>
                      )}

                      {/* Request Assistance Button */}
                      <button
                        onClick={onRequestAssistance}
                        disabled={!activeSessionId}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-purple-500 hover:text-white hover:bg-purple-500 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold"
                        title={t('chat.actions.requestAssistance')}
                      >
                        <Headset className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                        <span className="hidden sm:inline">{t('chat.actions.requestAssistance')}</span>
                      </button>
                    </div>
                  </div>
                </div>
                )}

                {/* Actions menu */}
                {showMobileMenu && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-[90]"
                      onClick={() => setShowMobileMenu(false)}
                    />

                    {/* Menu */}
                    <div className="absolute left-4 bottom-full mb-2 bg-white rounded-2xl shadow-2xl border border-gray-100 z-[95] min-w-[220px] overflow-hidden backdrop-blur-sm">
                      <button
                        onClick={() => {
                          onShowMobileOrders();
                          setShowMobileMenu(false);
                        }}
                        className="lg:hidden w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors text-sm text-left border-b border-gray-50"
                      >
                        <ShoppingBag className="w-4 h-4 text-gray-500" />
                        <span className="text-gray-700 font-medium">{t('chat.actions.viewOrders')}</span>
                      </button>

                      <button
                        onClick={() => {
                          onShowMobileCart();
                          setShowMobileMenu(false);
                        }}
                        className="md:hidden w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors text-sm text-left border-b border-gray-50"
                      >
                        <ShoppingBag className="w-4 h-4 text-gray-500" />
                        <div className="flex-1 flex items-center justify-between">
                          <span className="text-gray-700 font-medium">{t('chat.actions.viewCart', { defaultValue: 'View Cart' })}</span>
                          {cartItemsCount > 0 && (
                            <span className="bg-blue-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                              {cartItemsCount}
                            </span>
                          )}
                        </div>
                      </button>

                      {onResetChat && (
                        <button
                          onClick={() => {
                            onResetChat();
                            setShowMobileMenu(false);
                          }}
                          disabled={!activeSessionId || !hasMessages}
                          className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm text-left border-b border-gray-50"
                        >
                          <RotateCcw className="w-4 h-4 text-gray-500" />
                          <span className="text-gray-700 font-medium">{t('chat.actions.resetChat', { defaultValue: 'Reset Chat' })}</span>
                        </button>
                      )}

                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="hidden sm:block text-center mt-1 sm:mt-2">
            {/* Audio processing / error status */}
            {isProcessingAudio && (
              <p className="text-xs text-blue-500 font-medium mb-0.5 flex items-center justify-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                {t('chat.audio.processingStatus', { defaultValue: 'Processing audio…' })}
              </p>
            )}
            {audioError && (
              <p className="text-xs text-amber-600 font-medium mb-0.5">{audioError}</p>
            )}
            <p className="text-[10px] sm:text-xs text-gray-400 font-light">
              {t('chat.disclaimer')}
            </p>
          </div>
        </div>
      </div>



      {/* Image/PDF Zoom Modal */}
      {zoomedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setZoomedImage(null)}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setZoomedImage(null);
            }}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-all z-[101]"
            title={t('chat.closeZoom', { defaultValue: 'Close' })}
          >
            <X className="w-6 h-6" />
          </button>

          <div className="relative max-w-7xl max-h-full w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            {zoomedImage.mimeType === 'application/pdf' ? (
              <div className="bg-white rounded-lg shadow-2xl p-6 max-h-[85vh] overflow-auto w-full max-w-4xl">
                <PDFViewer
                  file={zoomedImage.preview}
                  maxWidth={750}
                />
              </div>
            ) : (
              <img
                src={zoomedImage.preview}
                alt={zoomedImage.name}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            )}
            {zoomedImage.name && (
              <div className="mt-4 bg-black/60 backdrop-blur-sm text-white text-sm px-4 py-2 rounded-lg max-w-full">
                <p className="truncate">{zoomedImage.name}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(ChatInput);
